<script setup>
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { required, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import PageHeader from '../../SettingsSubPageHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  channelName: {
    type: String,
    default: 'wechat_official',
  },
});

const store = useStore();
const getters = useStoreGetters();
const router = useRouter();
const { t } = useI18n();

const form = reactive({
  name: '',
  appId: '',
  corpId: '',
  agentId: '',
  secret: '',
  token: '',
  encodingAesKey: '',
});

const channelKind = computed(() => {
  return (
    {
      wechat: 'official_account',
      wechat_official: 'official_account',
      wechat_mini_program: 'mini_program',
      wecom: 'wecom',
    }[props.channelName] || 'official_account'
  );
});

const isWecom = computed(() => channelKind.value === 'wecom');
const isMiniProgram = computed(() => channelKind.value === 'mini_program');

const headerTitle = computed(() => {
  if (isWecom.value) return '创建企业微信接待渠道';
  if (isMiniProgram.value) return '创建微信小程序接待渠道';
  return t('INBOX_MGMT.ADD.WECHAT_CHANNEL.TITLE');
});

const headerContent = computed(() => {
  if (isWecom.value) return '企业微信渠道处于灰度测试中，请先用于内部联调。';
  if (isMiniProgram.value) return '微信小程序渠道处于灰度测试中，请先用于内部联调。';
  return t('INBOX_MGMT.ADD.WECHAT_CHANNEL.DESC');
});

const appIdLabel = computed(() => {
  if (isMiniProgram.value) return '小程序 AppID';
  return t('INBOX_MGMT.ADD.WECHAT_CHANNEL.APP_ID');
});

const secretLabel = computed(() => {
  if (isWecom.value) return '应用 Secret';
  return t('INBOX_MGMT.ADD.WECHAT_CHANNEL.APP_SECRET');
});

const webhookKind = computed(() => {
  return (
    {
      official_account: 'official',
      mini_program: 'mini-program',
      wecom: 'wecom',
    }[channelKind.value] || 'official'
  );
});

const rules = computed(() => ({
  name: { required },
  appId: { required: requiredIf(() => !isWecom.value) },
  corpId: { required: requiredIf(() => isWecom.value) },
  agentId: { required: requiredIf(() => isWecom.value) },
  secret: { required },
  token: { required },
  encodingAesKey: {},
}));

const v$ = useVuelidate(rules, form);
const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);
const callbackUrl = computed(
  () => `${window.location.origin}/webhooks/wechat/${webhookKind.value}`
);

const createChannel = async () => {
  const valid = await v$.value.$validate();
  if (!valid) return;

  try {
    const channel = {
      type: 'wechat',
      channel_kind: channelKind.value,
      secret: form.secret.trim(),
      token: form.token.trim(),
      encoding_aes_key: form.encodingAesKey.trim() || null,
    };

    if (isWecom.value) {
      channel.corp_id = form.corpId.trim();
      channel.agent_id = form.agentId.trim();
    } else {
      channel.app_id = form.appId.trim();
    }

    const inbox = await store.dispatch('inboxes/createChannel', {
      name: form.name.trim(),
      channel,
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: inbox.id },
    });
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.WECHAT_CHANNEL.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="headerTitle"
      :header-content="headerContent"
    />
    <form class="flex flex-col max-w-2xl gap-5" @submit.prevent="createChannel">
      <label :class="{ error: v$.name.$error }">
        {{ $t('INBOX_MGMT.ADD.WECHAT_CHANNEL.CHANNEL_NAME') }}
        <input v-model="form.name" type="text" @blur="v$.name.$touch" />
      </label>

      <label v-if="!isWecom" :class="{ error: v$.appId.$error }">
        {{ appIdLabel }}
        <input
          v-model="form.appId"
          type="text"
          autocomplete="off"
          @blur="v$.appId.$touch"
        />
      </label>

      <label v-if="isWecom" :class="{ error: v$.corpId.$error }">
        企业 ID（CorpID）
        <input
          v-model="form.corpId"
          type="text"
          autocomplete="off"
          @blur="v$.corpId.$touch"
        />
      </label>

      <label v-if="isWecom" :class="{ error: v$.agentId.$error }">
        应用 AgentId
        <input
          v-model="form.agentId"
          type="text"
          autocomplete="off"
          @blur="v$.agentId.$touch"
        />
      </label>

      <label :class="{ error: v$.secret.$error }">
        {{ secretLabel }}
        <input
          v-model="form.secret"
          type="password"
          autocomplete="new-password"
          @blur="v$.secret.$touch"
        />
      </label>

      <label :class="{ error: v$.token.$error }">
        {{ $t('INBOX_MGMT.ADD.WECHAT_CHANNEL.TOKEN') }}
        <input
          v-model="form.token"
          type="password"
          autocomplete="new-password"
          @blur="v$.token.$touch"
        />
      </label>

      <label>
        {{ $t('INBOX_MGMT.ADD.WECHAT_CHANNEL.ENCODING_AES_KEY') }}
        <input
          v-model="form.encodingAesKey"
          type="password"
          autocomplete="new-password"
        />
      </label>

      <label>
        {{ $t('INBOX_MGMT.ADD.WECHAT_CHANNEL.CALLBACK_URL') }}
        <input :value="callbackUrl" type="text" readonly />
      </label>

      <div>
        <Button
          type="submit"
          :is-loading="uiFlags.isCreating"
          :label="$t('INBOX_MGMT.ADD.WECHAT_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
